import { useState, useEffect, useMemo } from 'react';
import {
  Activity,
  Megaphone,
  Wallet,
  Users,
  Plus,
  Target,
  Calendar,
  ListTodo,
  ShieldAlert,
  TrendingUp,
  TrendingDown,
  Minus,
  ClipboardList,
  BarChart3,
  ChevronRight,
} from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip as RechartsTooltip, Legend } from 'recharts';
import { cn, formatCurrency, formatDate, statusColors } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { BudgetProgressBar, BudgetHealthIndicator } from '@/components/ui/budget-progress';
import { api } from '@/lib/api';
import { getAllPages } from '@/lib/paginated-data';
import { useAuthStore } from '@/stores/auth';
import { useGamificationStore } from '@/stores/gamification';
import { GamificationProgressCard } from '@/components/gamification/GamificationProgressCard';
import { GuidanceHubCard } from '@/components/onboarding/GuidanceHubCard';
import { CommandCenterStrip } from '@/components/dashboard/CommandCenterStrip';
import type { ApiResponse, MarketingActivity, Campaign, BudgetStatus } from '@/types';
import { InfoTooltip } from '@/components/help/InfoTooltip';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

interface DashboardStats {
  totalActivities: number;
  activeCampaigns: number;
  budgetUsed: number;
  leadsGenerated: number;
}

type QuickRange = 'week' | 'month' | 'quarter' | 'year' | 'custom';

interface DateRange {
  from: string;
  to: string;
}

function getQuickRange(range: QuickRange): DateRange {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  const fmt = (d: Date) =>
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

  if (range === 'week') {
    const day = now.getDay() === 0 ? 6 : now.getDay() - 1;
    const from = new Date(now);
    from.setDate(now.getDate() - day);
    const to = new Date(from);
    to.setDate(from.getDate() + 6);
    return { from: fmt(from), to: fmt(to) };
  }
  if (range === 'month') {
    return {
      from: fmt(new Date(now.getFullYear(), now.getMonth(), 1)),
      to: fmt(new Date(now.getFullYear(), now.getMonth() + 1, 0)),
    };
  }
  if (range === 'quarter') {
    const q = Math.floor(now.getMonth() / 3);
    return {
      from: fmt(new Date(now.getFullYear(), q * 3, 1)),
      to: fmt(new Date(now.getFullYear(), q * 3 + 3, 0)),
    };
  }
  if (range === 'year') {
    return {
      from: fmt(new Date(now.getFullYear(), 0, 1)),
      to: fmt(new Date(now.getFullYear(), 11, 31)),
    };
  }
  return { from: fmt(new Date(now.getFullYear(), now.getMonth(), 1)), to: fmt(now) };
}

function getPrevRange(range: DateRange): DateRange {
  const from = new Date(range.from);
  const to = new Date(range.to);
  const diffMs = to.getTime() - from.getTime() + 86400000;
  const prevTo = new Date(from.getTime() - 86400000);
  const prevFrom = new Date(prevTo.getTime() - diffMs + 86400000);
  const pad = (n: number) => String(n).padStart(2, '0');
  const fmt = (d: Date) =>
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  return { from: fmt(prevFrom), to: fmt(prevTo) };
}

function activityInRange(a: MarketingActivity, range: DateRange): boolean {
  const start = (a.start_date ?? a.created_at).slice(0, 10);
  const end = (a.end_date ?? a.start_date ?? a.created_at).slice(0, 10);
  return end >= range.from && start <= range.to;
}

function campaignInRange(c: Campaign, range: DateRange): boolean {
  const start = (c.start_date ?? c.created_at).slice(0, 10);
  const end = (c.end_date ?? c.start_date ?? c.created_at).slice(0, 10);
  return end >= range.from && start <= range.to;
}

function computeStats(
  activities: MarketingActivity[],
  campaigns: Campaign[],
  range: DateRange
): DashboardStats {
  const filteredActs = activities.filter((a) => activityInRange(a, range));
  const filteredCamps = campaigns.filter((c) => campaignInRange(c, range));
  return {
    totalActivities: filteredActs.length,
    activeCampaigns: filteredCamps.filter(
      (c) => c.status === 'published' || c.status === 'approved'
    ).length,
    budgetUsed: filteredActs.reduce((s, a) => s + (a.budget_actual ?? 0), 0),
    leadsGenerated: filteredActs.reduce((s, a) => s + (a.actual_leads ?? 0), 0),
  };
}

function TrendBadge({ current, prev }: { current: number; prev: number }) {
  if (prev === 0 && current === 0) return null;
  const diff = current - prev;
  const pct = prev === 0 ? null : Math.round((diff / prev) * 100);
  const label = pct !== null ? `${pct > 0 ? '+' : ''}${pct}%` : diff > 0 ? `+${diff}` : `${diff}`;
  if (diff > 0)
    return (
      <span className="flex items-center gap-0.5 text-xs font-medium text-success">
        <TrendingUp className="h-3 w-3" /> {label}
      </span>
    );
  if (diff < 0)
    return (
      <span className="flex items-center gap-0.5 text-xs font-medium text-danger">
        <TrendingDown className="h-3 w-3" /> {label}
      </span>
    );
  return (
    <span className="flex items-center gap-0.5 text-xs font-medium text-muted">
      <Minus className="h-3 w-3" /> 0%
    </span>
  );
}

const QUICK_RANGES: { key: QuickRange; label: string; hint: string }[] = [
  { key: 'week', label: 'Diese Woche', hint: 'Aktuelle Kalenderwoche (Montag bis Sonntag), begrenzt auf heute.' },
  { key: 'month', label: 'Dieser Monat', hint: 'Vom 1. bis zum letzten Tag des laufenden Monats.' },
  { key: 'quarter', label: 'Dieses Quartal', hint: 'Das aktuelle Quartal (Q1–Q4) von Monatsanfang bis Monatsende.' },
  { key: 'year', label: 'Dieses Jahr', hint: 'Kalenderjahr 1.1. bis 31.12.' },
  { key: 'custom', label: 'Benutzerdefiniert', hint: 'Freies Von–Bis-Datum für Vergleiche und Sondenauswertungen.' },
];

const BUDGET_COLORS = ['#2563eb', '#10b981', '#f59e0b'];

type DashboardInsightStep = {
  kind: string;
  severity: 'critical' | 'warning' | 'info';
  title: string;
  subtitle?: string;
  href: string;
};

type PlanVsActualRow = {
  plan_id: string;
  title: string;
  year: number;
  plan_budget_total: number;
  activities_budget_planned: number;
  activities_budget_actual: number;
  campaigns_budget_allocated: number;
  utilization_percent: number;
};

export function DashboardPage({ onNavigate }: { onNavigate: (path: string) => void }) {
  const user = useAuthStore((s) => s.user);
  const gamification = useGamificationStore((s) => s.profile);
  const gamificationLoading = useGamificationStore((s) => s.loading);
  const [allActivities, setAllActivities] = useState<MarketingActivity[]>([]);
  const [allCampaigns, setAllCampaigns] = useState<Campaign[]>([]);
  const [statsLoading, setStatsLoading] = useState(true);
  const [activitiesLoading, setActivitiesLoading] = useState(true);
  const [deadlinesLoading, setDeadlinesLoading] = useState(true);
  const [campaignsLoading, setCampaignsLoading] = useState(true);
  const [budgetLoading, setBudgetLoading] = useState(true);
  const [budgetHealth, setBudgetHealth] = useState<BudgetStatus | null>(null);
  const [insightsLoading, setInsightsLoading] = useState(true);
  const [nextSteps, setNextSteps] = useState<DashboardInsightStep[]>([]);
  const [planVsActual, setPlanVsActual] = useState<PlanVsActualRow[]>([]);

  const [quickRange, setQuickRange] = useState<QuickRange>('month');
  const [customRange, setCustomRange] = useState<DateRange>(() => getQuickRange('month'));

  const dateRange: DateRange = useMemo(
    () => (quickRange === 'custom' ? customRange : getQuickRange(quickRange)),
    [quickRange, customRange]
  );
  const prevRange = useMemo(() => getPrevRange(dateRange), [dateRange]);

  const stats = useMemo(
    () => computeStats(allActivities, allCampaigns, dateRange),
    [allActivities, allCampaigns, dateRange]
  );
  const prevStats = useMemo(
    () => computeStats(allActivities, allCampaigns, prevRange),
    [allActivities, allCampaigns, prevRange]
  );

  const recentActivities = useMemo(() => {
    const filtered = allActivities.filter((a) => activityInRange(a, dateRange));
    return [...filtered]
      .sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime())
      .slice(0, 5);
  }, [allActivities, dateRange]);

  const upcomingDeadlines = useMemo(() => {
    const withEnd = allActivities.filter(
      (a) => a.end_date && activityInRange(a, dateRange)
    );
    return [...withEnd]
      .sort((a, b) => new Date(a.end_date!).getTime() - new Date(b.end_date!).getTime())
      .slice(0, 5);
  }, [allActivities, dateRange]);

  const filteredCampaigns = useMemo(
    () => allCampaigns.filter((c) => c.is_active && campaignInRange(c, dateRange)).slice(0, 5),
    [allCampaigns, dateRange]
  );

  const budgetData = useMemo(() => {
    const filtered = allActivities.filter((a) => activityInRange(a, dateRange));
    const totalPlanned = filtered.reduce((s, a) => s + (a.budget_planned ?? 0), 0);
    const totalActual = filtered.reduce((s, a) => s + (a.budget_actual ?? 0), 0);
    const totalSupplier = filtered.reduce((s, a) => s + (a.budget_supplier ?? 0), 0);
    return [
      { name: 'Geplant', value: totalPlanned },
      { name: 'Tatsächlich', value: totalActual || 0 },
      { name: 'Zulieferer', value: totalSupplier || 0 },
    ];
  }, [allActivities, dateRange]);

  useEffect(() => {
    const load = async () => {
      setStatsLoading(true);
      try {
        const [activities, campaignsList] = await Promise.all([
          getAllPages<MarketingActivity>('/activities'),
          getAllPages<Campaign>('/campaigns'),
        ]);

        setAllActivities(activities);
        setAllCampaigns(campaignsList);

        try {
          const { data: plans } = await api.get<ApiResponse<{ id: string }[]>>('/marketing-plans?limit=1');
          const firstPlan = (plans as any)?.[0] ?? plans;
          if (firstPlan?.id) {
            const healthRes = await api.get<ApiResponse<BudgetStatus>>(`/budget-thresholds/status?plan_id=${firstPlan.id}`);
            if (healthRes.data) setBudgetHealth(healthRes.data);
          }
        } catch {}

      } catch {
        setAllActivities([]);
        setAllCampaigns([]);
      } finally {
        setStatsLoading(false);
        setActivitiesLoading(false);
        setDeadlinesLoading(false);
        setCampaignsLoading(false);
        setBudgetLoading(false);
      }
    };
    load();
  }, []);

  useEffect(() => {
    let cancelled = false;
    setInsightsLoading(true);
    void api
      .get<
        ApiResponse<{
          next_steps: DashboardInsightStep[];
          plan_vs_actual: PlanVsActualRow[];
        }>
      >('/dashboard-insights/summary')
      .then((res) => {
        if (cancelled || !res.success || !res.data) return;
        setNextSteps(Array.isArray(res.data.next_steps) ? res.data.next_steps : []);
        setPlanVsActual(Array.isArray(res.data.plan_vs_actual) ? res.data.plan_vs_actual : []);
      })
      .catch(() => {
        if (!cancelled) {
          setNextSteps([]);
          setPlanVsActual([]);
        }
      })
      .finally(() => {
        if (!cancelled) setInsightsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const name = user?.full_name || user?.email || 'Nutzer';

  const severityBadge = (s: DashboardInsightStep['severity']) => {
    if (s === 'critical')
      return 'bg-danger-soft text-danger';
    if (s === 'warning')
      return 'bg-warning-soft text-warning';
    return 'bg-card-hover text-muted dark:text-foreground';
  };

  return (
    <div className="space-y-8">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">
            Heute, {name}
          </h1>
        </div>

        {/* Date range filter */}
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-1.5 text-xs font-medium text-muted">
            <span>Zeitraum für Kennzahlen</span>
            <InfoTooltip text="Alle KPI-Karten und Trends unten beziehen sich auf diesen Zeitraum. Vergleichswerte nutzen jeweils den vorherigen gleich langen Zeitraum." />
          </div>
          <div className="flex flex-wrap gap-1.5">
            {QUICK_RANGES.map((r) => (
              <Tooltip key={r.key} delayDuration={200}>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={() => setQuickRange(r.key)}
                    className={cn(
                      'rounded-md px-3 py-1.5 text-xs font-medium transition-colors',
                      quickRange === r.key
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-card-hover text-muted hover:bg-border'
                    )}
                  >
                    {r.label}
                  </button>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="max-w-xs text-left text-sm">
                  {r.hint}
                </TooltipContent>
              </Tooltip>
            ))}
          </div>
          {quickRange === 'custom' && (
            <div className="flex items-center gap-2">
              <input
                type="date"
                value={customRange.from}
                onChange={(e) => setCustomRange((p) => ({ ...p, from: e.target.value }))}
                className="rounded-md border border-border bg-card px-2 py-1 text-xs dark:text-foreground"
              />
              <span className="text-xs text-muted">bis</span>
              <input
                type="date"
                value={customRange.to}
                onChange={(e) => setCustomRange((p) => ({ ...p, to: e.target.value }))}
                className="rounded-md border border-border bg-card px-2 py-1 text-xs dark:text-foreground"
              />
            </div>
          )}
          {quickRange !== 'custom' && (
            <p className="text-right text-xs text-muted">
              {dateRange.from} – {dateRange.to}
            </p>
          )}
        </div>
      </div>

      <CommandCenterStrip onNavigate={onNavigate} />

      <GuidanceHubCard />

      <div className="grid w-full gap-4 lg:grid-cols-2">
        <Card className="w-full min-w-0">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base">
              <ClipboardList className="h-5 w-5 shrink-0" />
              Nächste Schritte
              <InfoTooltip text="Priorisierte Hinweise: überfällige Termine, Reviews, nicht zugewiesene Aufgaben und baldige Deadlines. Klick führt zur Detailseite." />
            </CardTitle>
          </CardHeader>
          <CardContent>
            {insightsLoading ? (
              <div className="space-y-2">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-10 animate-pulse rounded bg-card-hover" />
                ))}
              </div>
            ) : nextSteps.length === 0 ? (
              <p className="text-sm text-muted">
                Keine offenen Prioritäten — gute Arbeit.
              </p>
            ) : (
              <ul className="space-y-2">
                {nextSteps.map((step, idx) => (
                  <li key={`${step.kind}-${idx}`}>
                    <button
                      type="button"
                      onClick={() => onNavigate(step.href)}
                      className="flex w-full min-w-0 items-start gap-2 rounded-lg border border-border px-3 py-2 text-left transition-colors hover:border-border hover:bg-background dark:hover:bg-card"
                    >
                      <Badge className={cn('shrink-0 text-[10px] uppercase', severityBadge(step.severity))}>
                        {step.severity === 'critical'
                          ? 'Dringend'
                          : step.severity === 'warning'
                            ? 'Wichtig'
                            : 'Info'}
                      </Badge>
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm font-medium text-foreground">{step.title}</span>
                        {step.subtitle ? (
                          <span className="mt-0.5 block text-xs text-muted">{step.subtitle}</span>
                        ) : null}
                      </span>
                      <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-muted" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        <Card className="w-full min-w-0">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base">
              <BarChart3 className="h-5 w-5 shrink-0" />
              Plan vs. Ist (Budget)
              <InfoTooltip text="Planbudget des Jahresplans gegen summierte Ist-Ausgaben aus Aktivitäten. Kampagnen-Budget zeigt zugewiesene Kampagnenbudgets im gleichen Plan." />
            </CardTitle>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            {insightsLoading ? (
              <div className="h-32 animate-pulse rounded bg-card-hover" />
            ) : planVsActual.length === 0 ? (
              <p className="text-sm text-muted">Keine aktiven Pläne erfasst.</p>
            ) : (
              <table className="w-full min-w-[520px] text-left text-sm">
                <thead>
                  <tr className="border-b border-border text-xs text-muted">
                    <th className="py-2 pr-2 font-medium">Plan</th>
                    <th className="py-2 pr-2 font-medium">Plan-Budget</th>
                    <th className="py-2 pr-2 font-medium">Ist (Akt.)</th>
                    <th className="py-2 pr-2 font-medium">Kampagnen</th>
                    <th className="py-2 font-medium">Auslastung</th>
                  </tr>
                </thead>
                <tbody>
                  {planVsActual.map((row) => (
                    <tr key={row.plan_id} className="border-b border-border">
                      <td className="py-2 pr-2 font-medium text-foreground">
                        <button
                          type="button"
                          className="text-left hover:underline"
                          onClick={() => onNavigate('/marketing-plan')}
                        >
                          {row.title}{' '}
                          <span className="text-muted">({row.year})</span>
                        </button>
                      </td>
                      <td className="py-2 pr-2 tabular-nums">{formatCurrency(row.plan_budget_total)}</td>
                      <td className="py-2 pr-2 tabular-nums">{formatCurrency(row.activities_budget_actual)}</td>
                      <td className="py-2 pr-2 tabular-nums">{formatCurrency(row.campaigns_budget_allocated)}</td>
                      <td className="py-2 tabular-nums">
                        <span
                          className={cn(
                            row.utilization_percent >= 100
                              ? 'text-danger'
                              : row.utilization_percent >= 80
                                ? 'text-warning'
                                : 'text-success'
                          )}
                        >
                          {row.utilization_percent}%
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Quick actions */}
      <div className="flex flex-wrap items-center gap-2">
        <Tooltip delayDuration={200}>
          <TooltipTrigger asChild>
            <span className="inline-flex">
              <Button
                onClick={() => {
                  window.localStorage.setItem('leftclick_open_create_activity', '1');
                  onNavigate('/activities');
                }}
              >
                <Plus className="h-4 w-4" />
                Neue Aktivität
              </Button>
            </span>
          </TooltipTrigger>
          <TooltipContent className="max-w-xs text-left text-sm">
            Legt im Bereich Aktivitäten eine neue Aufgabe an (Plan/Zuweisung dort wählen). Öffnet die Liste mit
            vorausgefülltem Erstellungsdialog.
          </TooltipContent>
        </Tooltip>
        <Tooltip delayDuration={200}>
          <TooltipTrigger asChild>
            <span className="inline-flex">
              <Button
                variant="outline"
                onClick={() => {
                  window.localStorage.setItem('leftclick_open_create_campaign', '1');
                  onNavigate('/campaigns');
                }}
              >
                <Megaphone className="h-4 w-4" />
                Neue Kampagne
              </Button>
            </span>
          </TooltipTrigger>
          <TooltipContent className="max-w-xs text-left text-sm">
            Startet die Kampagnenerstellung. Kampagnen bündeln Inhalte und Massnahmen unter einem gemeinsamen Ziel.
          </TooltipContent>
        </Tooltip>
        <Tooltip delayDuration={200}>
          <TooltipTrigger asChild>
            <span className="inline-flex">
              <Button variant="outline" onClick={() => onNavigate('/marketing-plan')}>
                <Target className="h-4 w-4" />
                Marketingplan anzeigen
              </Button>
            </span>
          </TooltipTrigger>
          <TooltipContent className="max-w-xs text-left text-sm">
            Zum Jahresplan mit Phasen, Budget und Aktivitäten — die operative Steuerung neben der Strategie-Seite.
          </TooltipContent>
        </Tooltip>
      </div>

      <GamificationProgressCard profile={gamification} loading={gamificationLoading} className="w-full" />

      {/* Stats row */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="flex items-center gap-1.5 text-sm font-medium text-muted">
              <span>Aktivitäten gesamt</span>
              <InfoTooltip text="Alle Aktivitäten im gewählten Zeitraum (nicht nur „aktiv“). Für Details in die Aktivitätenliste wechseln." />
            </CardTitle>
            <Activity className="h-4 w-4 text-muted" />
          </CardHeader>
          <CardContent>
            {statsLoading ? (
              <div className="h-8 w-16 animate-pulse rounded bg-card-hover" />
            ) : (
              <div className="flex items-end justify-between">
                <span className="text-2xl font-bold">{stats.totalActivities}</span>
                <TrendBadge current={stats.totalActivities} prev={prevStats.totalActivities} />
              </div>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="flex items-center gap-1.5 text-sm font-medium text-muted">
              <span>Aktive Kampagnen</span>
              <InfoTooltip text="Kampagnen mit Status „aktiv“ im gewählten Zeitraum. Trends vergleichen mit dem vorherigen gleichen Zeitraum." />
            </CardTitle>
            <Megaphone className="h-4 w-4 text-muted" />
          </CardHeader>
          <CardContent>
            {statsLoading ? (
              <div className="h-8 w-16 animate-pulse rounded bg-card-hover" />
            ) : (
              <div className="flex items-end justify-between">
                <span className="text-2xl font-bold">{stats.activeCampaigns}</span>
                <TrendBadge current={stats.activeCampaigns} prev={prevStats.activeCampaigns} />
              </div>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="flex items-center gap-1.5 text-sm font-medium text-muted">
              <span>Budget verbraucht</span>
              <InfoTooltip text="Summe der erfassten Ist-Ausgaben aus Aktivitäten/Kampagnen im Zeitraum — nur so aussagekräftig wie die Datenpflege im Plan." />
            </CardTitle>
            <Wallet className="h-4 w-4 text-muted" />
          </CardHeader>
          <CardContent>
            {statsLoading ? (
              <div className="h-8 w-24 animate-pulse rounded bg-card-hover" />
            ) : (
              <div className="flex items-end justify-between">
                <span className="text-2xl font-bold">{formatCurrency(stats.budgetUsed)}</span>
                <TrendBadge current={stats.budgetUsed} prev={prevStats.budgetUsed} />
              </div>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="flex items-center gap-1.5 text-sm font-medium text-muted">
              <span>Leads generiert</span>
              <InfoTooltip text="Summe der in Aktivitäten erfassten Lead-Zahlen (Ist) im Zeitraum. Definition „Lead“ sollte im Team einheitlich sein." />
            </CardTitle>
            <Users className="h-4 w-4 text-muted" />
          </CardHeader>
          <CardContent>
            {statsLoading ? (
              <div className="h-8 w-16 animate-pulse rounded bg-card-hover" />
            ) : (
              <div className="flex items-end justify-between">
                <span className="text-2xl font-bold">{stats.leadsGenerated}</span>
                <TrendBadge current={stats.leadsGenerated} prev={prevStats.leadsGenerated} />
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* 2-column: Recent activities + Upcoming deadlines */}
      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ListTodo className="h-5 w-5" />
              Letzte Aktivitäten
            </CardTitle>
            <CardContent className="pt-0">
              {activitiesLoading ? (
                <div className="space-y-2">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <div
                      key={i}
                      className="h-12 animate-pulse rounded bg-card-hover"
                    />
                  ))}
                </div>
              ) : recentActivities.length === 0 ? (
                <p className="text-sm text-muted">
                  Keine Aktivitäten im gewählten Zeitraum.
                </p>
              ) : (
                <ul className="space-y-2">
                  {recentActivities.map((a) => (
                    <li
                      key={a.id}
                      className="flex items-center justify-between rounded-lg border border-border py-2 px-3"
                    >
                      <span className="truncate text-sm font-medium">{a.title}</span>
                      <Badge className={cn('ml-2 shrink-0', statusColors[a.status] || '')}>
                        {a.status}
                      </Badge>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </CardHeader>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Bevorstehende Fristen
            </CardTitle>
            <CardContent className="pt-0">
              {deadlinesLoading ? (
                <div className="space-y-2">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <div
                      key={i}
                      className="h-12 animate-pulse rounded bg-card-hover"
                    />
                  ))}
                </div>
              ) : upcomingDeadlines.length === 0 ? (
                <p className="text-sm text-muted">
                  Keine Fristen im gewählten Zeitraum.
                </p>
              ) : (
                <ul className="space-y-2">
                  {upcomingDeadlines.map((a) => (
                    <li
                      key={a.id}
                      className="flex items-center justify-between rounded-lg border border-border py-2 px-3"
                    >
                      <span className="truncate text-sm font-medium">{a.title}</span>
                      <span className="text-xs text-muted">
                        {a.end_date ? formatDate(a.end_date) : '-'}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </CardHeader>
        </Card>
      </div>

      {/* Budget Health */}
      {budgetHealth && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ShieldAlert className="h-5 w-5" />
              Budget-Status
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <BudgetProgressBar
                label="Gesamtplan"
                total={budgetHealth.plan.total}
                actual={budgetHealth.plan.actual}
              />
              {budgetHealth.suppliers.length > 0 && (
                <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                  {budgetHealth.suppliers.slice(0, 6).map((s) => (
                    <BudgetHealthIndicator
                      key={s.supplier_id}
                      level={s.level}
                      label={`Zulieferer`}
                      percent={s.percent}
                    />
                  ))}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Budget chart + Active campaigns */}
      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Budget-Übersicht</CardTitle>
          </CardHeader>
          <CardContent>
            {budgetLoading ? (
              <div className="flex h-[200px] items-center justify-center">
                <div className="h-10 w-10 animate-spin rounded-full border-4 border-border border-t-primary-600 dark:border-t-primary-400" />
              </div>
            ) : (
              <div className="h-[200px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={budgetData}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={80}
                      paddingAngle={2}
                      dataKey="value"
                      label={({ name, value }) =>
                        value ? `${name}: ${formatCurrency(value)}` : name
                      }
                    >
                      {budgetData.map((_, i) => (
                        <Cell key={i} fill={BUDGET_COLORS[i % BUDGET_COLORS.length]} />
                      ))}
                    </Pie>
                    <RechartsTooltip formatter={(value: number) => formatCurrency(value)} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Aktive Kampagnen</CardTitle>
          </CardHeader>
          <CardContent>
            {campaignsLoading ? (
              <div className="space-y-2">
                {[1, 2, 3].map((i) => (
                  <div
                    key={i}
                    className="h-10 animate-pulse rounded bg-card-hover"
                  />
                ))}
              </div>
            ) : filteredCampaigns.length === 0 ? (
              <p className="text-sm text-muted">
                Keine aktiven Kampagnen im gewählten Zeitraum.
              </p>
            ) : (
              <ul className="space-y-2">
                {filteredCampaigns.map((c) => (
                  <li
                    key={c.id}
                    className="flex items-center justify-between rounded-lg border border-border py-2 px-3"
                  >
                    <span className="truncate text-sm font-medium">{c.name}</span>
                    <Badge variant="secondary">{c.status}</Badge>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
